import { createMediaItemMutation } from '@/graphql/mutations/Media.mutation';
import type { MediaItem } from '@/graphql/types';
import type Konva from 'konva';
import { toast } from 'react-toastify';
import { useCanvasStore } from '../store/canvasStore';
import type { AnimationType, CanvasImage, LogoProps, TextItem } from '../types';
import { GifGenerator } from './GifGenerator';

export interface CanvasMediaItemInput {
  id?: string;
  mediaItemUrl?: string;
  title?: string | null;
  description?: string;
  format?: string;
}

export const getGifAnimationDuration = (): number => {
  return useCanvasStore.getState().gifAnimationDuration;
};

export const setGifAnimationDuration = (seconds: number): void => {
  useCanvasStore.getState().setGifAnimationDuration(seconds);
};

export interface OriginalAnimationProps {
  texts: Array<{ id: string; duration: number }>;
  images: Array<{ id: string; duration: number }>;
  logo?: { duration: number } | null;
}

export const adjustAnimationDurationsForGif = (customDuration?: number): OriginalAnimationProps => {
  const state = useCanvasStore.getState();
  const originalProps: OriginalAnimationProps = {
    texts: [],
    images: [],
    logo: null,
  };

  const duration = customDuration !== undefined ? customDuration : getGifAnimationDuration();

  let longestEntrance = 0;
  let longestLoop = 0;

  state.texts.forEach((text) => {
    if (text.animation && text.animation !== 'none') {
      const animDuration = typeof text.animationDuration === 'number' ? text.animationDuration : 1;
      if (text.animation.includes('from')) {
        longestEntrance = Math.max(longestEntrance, animDuration);
      } else {
        longestLoop = Math.max(longestLoop, animDuration);
      }
    }
  });

  state.canvasImages.forEach((img) => {
    if (img.animation && img.animation !== 'none') {
      const animDuration = typeof img.animationDuration === 'number' ? img.animationDuration : 1;
      if (img.animation.includes('from')) {
        longestEntrance = Math.max(longestEntrance, animDuration);
      } else {
        longestLoop = Math.max(longestLoop, animDuration);
      }
    }
  });

  if (state.logoProps?.animation && state.logoProps.animation !== 'none') {
    const animDuration = typeof state.logoProps.animationDuration === 'number' ? state.logoProps.animationDuration : 1;
    if (state.logoProps.animation.includes('from')) {
      longestEntrance = Math.max(longestEntrance, animDuration);
    } else {
      longestLoop = Math.max(longestLoop, animDuration);
    }
  }

  const targetEntranceDuration = duration * 0.4;
  const targetLoopDuration = duration * 0.6;

  const entranceScaleFactor = longestEntrance > 0 ? targetEntranceDuration / longestEntrance : 1;
  const loopScaleFactor = longestLoop > 0 ? targetLoopDuration / longestLoop : 1;

  const updatedTexts = state.texts.map((text) => {
    if (text.animation && text.animation !== 'none') {
      const originalDuration = text.animationDuration || 1;
      originalProps.texts.push({ id: text.id, duration: originalDuration });

      const isEntranceAnimation = text.animation.includes('from');
      const scaleFactor = isEntranceAnimation ? entranceScaleFactor : loopScaleFactor;

      const newDuration = originalDuration * scaleFactor;

      return { ...text, animationDuration: newDuration };
    }
    return text;
  });

  const updatedImages = state.canvasImages.map((img) => {
    if (img.animation && img.animation !== 'none') {
      const originalDuration = img.animationDuration || 1;
      originalProps.images.push({ id: img.id, duration: originalDuration });

      const isEntranceAnimation = img.animation.includes('from');
      const scaleFactor = isEntranceAnimation ? entranceScaleFactor : loopScaleFactor;

      const newDuration = originalDuration * scaleFactor;

      return { ...img, animationDuration: newDuration };
    }
    return img;
  });

  let updatedLogoProps = state.logoProps;
  if (state.logoProps?.animation && state.logoProps.animation !== 'none') {
    const originalDuration =
      typeof state.logoProps.animationDuration === 'number' ? state.logoProps.animationDuration : 1;

    originalProps.logo = { duration: originalDuration };

    const isEntranceAnimation = state.logoProps.animation.includes('from');
    const scaleFactor = isEntranceAnimation ? entranceScaleFactor : loopScaleFactor;

    const newDuration = originalDuration * scaleFactor;

    updatedLogoProps = { ...state.logoProps, animationDuration: newDuration };
  }

  useCanvasStore.setState({
    texts: updatedTexts,
    canvasImages: updatedImages,
    logoProps: updatedLogoProps,
  });

  return originalProps;
};

export const restoreAnimationDurations = (originalProps: OriginalAnimationProps): void => {
  const state = useCanvasStore.getState();

  const restoredTexts = state.texts.map((text) => {
    const originalText = originalProps.texts.find((t) => t.id === text.id);
    return originalText ? { ...text, animationDuration: originalText.duration } : text;
  });

  const restoredImages = state.canvasImages.map((img) => {
    const originalImage = originalProps.images.find((i) => i.id === img.id);
    return originalImage ? { ...img, animationDuration: originalImage.duration } : img;
  });

  let restoredLogoProps = state.logoProps;
  if (originalProps.logo && state.logoProps) {
    restoredLogoProps = {
      ...state.logoProps,
      animationDuration: originalProps.logo.duration,
    };
  }

  useCanvasStore.setState({
    texts: restoredTexts,
    canvasImages: restoredImages,
    logoProps: restoredLogoProps,
  });
};

const generateGif = async (stage: Konva.Stage): Promise<Blob> => {
  return new Promise<Blob>((resolve, reject) => {
    try {
      const originalProps = adjustAnimationDurationsForGif();

      setTimeout(async () => {
        try {
          const animationStore = useCanvasStore.getState();
          const hasEntranceAnimations = [...animationStore.texts, ...animationStore.canvasImages].some((item) =>
            item.animation?.includes('from')
          );

          let targetFps = 15;
          if (hasEntranceAnimations || getGifAnimationDuration() <= 3) {
            targetFps = 24;
          } else if (getGifAnimationDuration() > 8) {
            targetFps = 12;
          }

          const gifGenerator = new GifGenerator(stage, {
            duration: getGifAnimationDuration(),
            framesPerSecond: targetFps,
            quality: 1.8,
          });

          gifGenerator.prepareStageForExport();

          try {
            const frames = await gifGenerator.captureFrames();
            const blob = await gifGenerator.processFrames(frames, false);

            restoreAnimationDurations(originalProps);

            if (blob && blob.size > 0) {
              resolve(blob);
            } else {
              reject(null);
            }
          } finally {
            gifGenerator.restoreStage();
          }
        } catch (error) {
          restoreAnimationDurations(originalProps);
          reject(null);
        }
      }, 150);
    } catch (error) {
      reject(null);
    }
  });
};

const hasAnimations = (state: ReturnType<typeof useCanvasStore.getState>): boolean => {
  const hasTextAnimations = state.texts.some((t) => t.animation && t.animation !== 'none');
  const hasLogoAnimation = state.logoProps?.animation !== 'none' && !!state.logoImage;
  const hasImageAnimations = state.canvasImages.some(
    (img) => img.animation && img.animation !== 'none' && !img.deleted
  );

  return hasTextAnimations || hasLogoAnimation || hasImageAnimations;
};

export const saveCanvasAsMedia = async (
  title = 'Canvas Thumbnail',
  format = 'png',
  existingId?: string
): Promise<boolean> => {
  const validFormats = ['png', 'jpeg', 'gif', 'webp'];
  if (!validFormats.includes(format)) {
    toast.error(`Invalid format: ${format}`);
    return false;
  }

  const state = useCanvasStore.getState();
  const currentState = {
    previewMode: state.previewMode,
    selectedId: state.selectedId,
    editingTextId: state.isEditingTextId,
  };

  try {
    useCanvasStore.setState({
      selectedId: null,
      isEditingTextId: null,
    });

    await new Promise((resolve) => setTimeout(resolve, 150));

    const stage = useCanvasStore.getState().stageRef.current;
    if (!stage) {
      return false;
    }

    stage.batchDraw();

    let file: File | null;

    if (format === 'gif' && hasAnimations(useCanvasStore.getState())) {
      try {
        const blob = await generateGif(stage);
        if (blob && blob.size > 0) {
          file = new File([blob], `${title}.gif`, { type: 'image/gif' });

          if (!file && blob.size < 1000000) {
            try {
              const reader = new FileReader();
              const dataUrl = await new Promise<string>((resolve) => {
                reader.onload = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
              });
              file = dataURLtoFile(dataUrl, `${title}.gif`);
            } catch (conversionError) {}
          }
        } else {
          throw new Error('Generated GIF was empty');
        }
      } catch (gifError) {
        toast.error('Failed to create animated GIF, using static image');
        const dataURL = stage.toDataURL();
        file = dataURLtoFile(dataURL, `${title}.png`);
        if (!file) return false;
      }
    } else {
      const originalPreviewMode = useCanvasStore.getState().previewMode;

      useCanvasStore.setState({ previewMode: false });

      await new Promise((resolve) => setTimeout(resolve, 100));

      try {
        const dataURL = stage.toDataURL({
          mimeType: `image/${format}`,
          quality: format === 'jpeg' ? 0.95 : undefined,
          pixelRatio: 2,
        });

        file = dataURLtoFile(dataURL, `${title}.${format}`);
      } catch (error) {
        toast.error(`Error creating ${format.toUpperCase()} image`);
        return false;
      } finally {
        useCanvasStore.setState({ previewMode: originalPreviewMode });
      }

      if (!file) {
        return false;
      }
    }

    const canvasState = serializeCanvasState();
    const safeTitle = title.replace(/[<>]/g, '');
    const description = `ZNGLY_CANVAS_EDITOR_STATE=${canvasState}`;

    return new Promise((resolve) => {
      createMediaItemMutation(
        {
          file,
          title: safeTitle,
          ...(existingId ? { id: existingId } : {}),
          description,
          altText: safeTitle,
        },
        [],
        (response, errors) => {
          if (errors?.length) {
            toast.error(`Error saving image: ${errors.map((e) => e.message).join(', ')}`);
            resolve(false);
            return;
          }

          if (!response?.newMediaItem?.mediaItem) {
            toast.error('Failed to save image');
            resolve(false);
            return;
          }

          toast.success('Image saved successfully!');
          resolve(true);
        }
      );
    });
  } catch (error) {
    toast.error('Failed to save image');
    return false;
  } finally {
    useCanvasStore.setState({
      previewMode: currentState.previewMode,
      selectedId: currentState.selectedId,
      isEditingTextId: currentState.editingTextId,
    });
  }
};

export const isCanvasThumbnail = (mediaItem: CanvasMediaItemInput | MediaItem): boolean => {
  if (!mediaItem?.description) return false;

  const containsEditorState = ['ZNGLY_CANVAS_EDITOR_STATE=', 'ZNGLY_CANVAS_STATE=', '"isZnglyCanvas":true'].some(
    (marker) => mediaItem.description?.includes(marker) ?? false
  );

  if (containsEditorState) return true;

  return typeof mediaItem?.title === 'string' && mediaItem.title.toLowerCase().includes('canvas thumbnail');
};

export const loadCanvasStateFromMedia = (mediaItem: CanvasMediaItemInput | MediaItem): boolean => {
  if (!mediaItem?.description) return false;

  try {
    const description = mediaItem.description;
    let jsonStr = '';
    let success = false;

    if (description.includes('ZNGLY_CANVAS_EDITOR_STATE=')) {
      const startIndex = description.indexOf('ZNGLY_CANVAS_EDITOR_STATE=') + 'ZNGLY_CANVAS_EDITOR_STATE='.length;
      jsonStr = description.substring(startIndex);
      jsonStr = stripHtmlTags(jsonStr);
      jsonStr = decodeHtmlEntities(jsonStr);
      jsonStr = fixWordPressJsonEncoding(jsonStr);

      try {
        const stateData = JSON.parse(jsonStr);
        success = applyCanvasData(stateData);
        if (success) return true;
      } catch (error) {
        try {
          const cleanJsonStr = jsonStr.replace(/[^\x20-\x7E]/g, '');
          const stateData = JSON.parse(cleanJsonStr);
          success = applyCanvasData(stateData);
          if (success) return true;
        } catch (e) {
          // Continue to next format
        }
      }
    }

    // Try other formats...

    return success;
  } catch (error) {
    return false;
  }
};

const applyCanvasData = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') return false;

  try {
    const typedData = data as Partial<{
      version: string;
      backgroundColor: string;
      bgImage: string | null;
      texts: TextItem[];
      images: CanvasImage[];
      logoImage: string | null;
      logoProps: LogoProps | null;
    }>;

    const store = useCanvasStore.getState();
    store.resetCanvas();

    const originalZIndices: Record<string, number> = {};

    if (Array.isArray(typedData.texts)) {
      typedData.texts.forEach((text) => {
        if (text.id && typeof text.zIndex === 'number') {
          originalZIndices[`text-${text.id}`] = text.zIndex;
        }
      });
    }

    if (Array.isArray(typedData.images)) {
      typedData.images.forEach((img) => {
        if (img.id && typeof img.zIndex === 'number') {
          originalZIndices[`image-${img.id}`] = img.zIndex;
        }
      });
    }

    if (typedData.backgroundColor && typeof typedData.backgroundColor === 'string') {
      store.setBackgroundColor(typedData.backgroundColor);
    }

    if (typedData.bgImage && typeof typedData.bgImage === 'string') {
      store.setBgImage(typedData.bgImage);
    }

    if (Array.isArray(typedData.texts)) {
      const validTexts = typedData.texts
        .filter((text) => typeof text === 'object' && text && 'id' in text)
        .map((text) => ({
          ...text,
          align: ['left', 'center', 'right'].includes(text.align as string)
            ? (text.align as 'left' | 'center' | 'right')
            : 'left',
          animation: typeof text.animation === 'string' ? (text.animation as AnimationType) : 'none',
          animationDuration: text.animationDuration || 1,
          animationDelay: text.animationDelay || 0,
          zIndex: text.zIndex || 1,
        })) as unknown as TextItem[];

      store.setTexts(validTexts);
    }

    if (Array.isArray(typedData.images) && typedData.images.length > 0) {
      const loadImage = async (img: ImageToLoad): Promise<string | null> => {
        if (img && typeof img.url === 'string') {
          const metadata = {
            originalId: img.id,
            originalZIndex: img.zIndex,
          };

          const id = await store.addImage(img.url, { metadata } as Record<string, unknown>);

          if (id) {
            store.updateImage(id, {
              x: typeof img.x === 'number' ? img.x : 0,
              y: typeof img.y === 'number' ? img.y : 0,
              width: typeof img.width === 'number' ? img.width : 200,
              height: typeof img.height === 'number' ? img.height : 200,
              opacity: typeof img.opacity === 'number' ? img.opacity : 1,
              rotation: typeof img.rotation === 'number' ? img.rotation : 0,
              animation: typeof img.animation === 'string' ? (img.animation as AnimationType) : 'none',
              zIndex: typeof img.zIndex === 'number' ? img.zIndex : 1,
              animationDuration: typeof img.animationDuration === 'number' ? img.animationDuration : 1,
              animationDelay: typeof img.animationDelay === 'number' ? img.animationDelay : 0,
              metadata,
            } as Partial<CanvasImage>);
            return id;
          }
        }
        return null;
      };

      (async () => {
        if (typedData.images) {
          // Sort images by z-index before loading them
          const sortedImages = [...typedData.images].sort(
            (a, b) => (typeof a.zIndex === 'number' ? a.zIndex : 1) - (typeof b.zIndex === 'number' ? b.zIndex : 1)
          );

          for (const img of sortedImages) {
            await loadImage(img);
          }

          // Wait for all images to fully load
          await Promise.all(typedData.images.map((img) => waitForImageLoad(img.url)));

          const store = useCanvasStore.getState();

          const allElements = [
            ...store.texts.map((text) => ({
              type: 'text' as const,
              id: text.id,
              zIndex: text.zIndex || 1,
              originalZIndex: originalZIndices[`text-${text.id}`] || text.zIndex || 1,
            })),
            ...store.canvasImages
              .filter((img) => !img.deleted)
              .map((img) => ({
                type: 'image' as const,
                id: img.id,
                zIndex: img.zIndex || 1,
                originalId: (img as CanvasImageWithMetadata).metadata?.originalId,
                originalZIndex:
                  (img as CanvasImageWithMetadata).metadata?.originalZIndex ||
                  originalZIndices[`image-${(img as CanvasImageWithMetadata).metadata?.originalId}`] ||
                  img.zIndex ||
                  1,
              })),
          ];

          allElements.sort((a, b) => a.originalZIndex - b.originalZIndex);

          let zIndex = 1;
          for (const element of allElements) {
            if (element.type === 'text') {
              store.setTexts(
                (prev) => prev.map((text) => (text.id === element.id ? { ...text, zIndex } : text)),
                false
              );
            } else if (element.type === 'image') {
              store.updateImage(element.id, { zIndex });
            }
            zIndex++;
          }

          setTimeout(() => {
            const finalStore = useCanvasStore.getState();
            finalStore.normalizeZIndices();
            finalStore.updateElementOrder();
          }, 300);
        }
      })();
    }

    if (
      typedData.logoImage &&
      typeof typedData.logoImage === 'string' &&
      typedData.logoProps &&
      typeof typedData.logoProps === 'object'
    ) {
      store.setLogoImage(typedData.logoImage);
      const props = typedData.logoProps as Partial<LogoProps>;

      if (
        typeof props.x === 'number' &&
        typeof props.y === 'number' &&
        typeof props.width === 'number' &&
        typeof props.height === 'number'
      ) {
        const validLogoProps: LogoProps = {
          x: props.x,
          y: props.y,
          width: props.width,
          height: props.height,
          animation: typeof props.animation === 'string' ? (props.animation as AnimationType) : 'none',
        };

        store.setLogoProps(validLogoProps);
      }
    }

    return true;
  } catch (e) {
    return false;
  }
};

interface CanvasImageWithMetadata extends CanvasImage {
  metadata?: {
    originalId?: string;
    originalZIndex?: number;
  };
}

interface OrderedElement {
  type: 'text' | 'image';
  id: string | null;
  zIndex: number;
  originalId?: string;
  url?: string;
}

interface ImageIdMap {
  [key: string]: string;
}

interface ImageToLoad {
  id?: string;
  url: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  opacity?: number;
  rotation?: number;
  animation?: string;
  zIndex?: number;
  animationDuration?: number;
  animationDelay?: number;
}

const dataURLtoFile = (dataurl: string, filename: string): File | null => {
  if (!dataurl?.startsWith('data:image/')) return null;

  try {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    const u8arr = new Uint8Array(bstr.length);

    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }

    return new File([u8arr], filename, { type: mime });
  } catch {
    return null;
  }
};

const serializeCanvasState = (): string => {
  const state = useCanvasStore.getState();

  const serializedTexts = state.texts.map(
    ({
      id,
      text,
      x,
      y,
      width,
      color,
      fontFamily,
      fontSize,
      lineHeight,
      letterSpacing,
      align,
      animation,
      zIndex,
      animationDuration,
      animationDelay,
    }) => ({
      id,
      text,
      x,
      y,
      width,
      color,
      fontFamily,
      fontSize,
      lineHeight,
      letterSpacing,
      align,
      animation,
      zIndex: zIndex || 1,
      animationDuration: animationDuration || 1,
      animationDelay: animationDelay || 0,
    })
  );

  const serializedImages = state.canvasImages.map(
    ({ id, url, x, y, width, height, opacity, rotation, animation, zIndex, animationDuration, animationDelay }) => ({
      id,
      url,
      x,
      y,
      width,
      height,
      opacity: opacity || 1,
      rotation: rotation || 0,
      animation: animation || 'none',
      zIndex: zIndex || 1,
      animationDuration: animationDuration || 1,
      animationDelay: animationDelay || 0,
    })
  );

  const logoProps = state.logoProps
    ? {
        ...state.logoProps,
        animationDuration: state.logoProps.animationDuration || 1,
        animationDelay: state.logoProps.animationDelay || 0,
      }
    : null;

  return JSON.stringify({
    version: '1.0',
    backgroundColor: state.backgroundColor,
    bgImage: state.bgImage,
    texts: serializedTexts,
    images: serializedImages,
    logoImage: state.logoImage,
    logoProps: logoProps,
  });
};

const stripHtmlTags = (html: string): string => {
  return html.replace(/<\/?[^>]+(>|$)/g, '');
};

const decodeHtmlEntities = (input: string): string => {
  try {
    const doc = new DOMParser().parseFromString(input, 'text/html');
    return doc.documentElement.textContent || input;
  } catch (e) {
    return input;
  }
};

const fixWordPressJsonEncoding = (jsonStr: string): string => {
  return jsonStr
    .replace(/[\u201C\u201D\u2033\u201E\u2036]/g, '"')
    .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\u2026/g, '...');
};

export function applyCustomDurationToGif(
  stage: Konva.Stage,
  showProgress: boolean,
  callback?: (blob: Blob | null) => void,
  options?: { duration?: number; quality?: number; enhancedMode?: boolean }
): Promise<Blob | null> {
  const duration = options?.duration || getGifAnimationDuration();
  const quality = options?.quality || 1.8;

  const animationStore = useCanvasStore.getState();
  const hasEntranceAnimations = [...animationStore.texts, ...animationStore.canvasImages].some((item) =>
    item.animation?.includes('from')
  );

  let fps = 15;
  if (hasEntranceAnimations || duration <= 3) {
    fps = 24;
  } else if (duration > 8) {
    fps = 12;
  }

  const originalProps = adjustAnimationDurationsForGif(duration);

  return new Promise<Blob | null>((resolve) => {
    // Create a new GifGenerator instance with the appropriate options
    const gifGenerator = new GifGenerator(stage, {
      duration,
      framesPerSecond: fps,
      quality,
    });

    gifGenerator
      .generateGif(showProgress)
      .then((blob) => {
        restoreAnimationDurations(originalProps);

        if (callback) callback(blob);
        resolve(blob);
      })
      .catch((error) => {
        restoreAnimationDurations(originalProps);

        if (callback) callback(null);
        resolve(null);
      });
  });
}

const waitForImageLoad = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => {
      resolve();
    };
    img.src = src;
  });
};
